/**
 * SEO helpers: set document title, meta tags, canonical, OG/Twitter, and a route-scoped
 * JSON-LD block. Used from page components via useDocumentHead().
 *
 * We're an SPA, so on every navigation we mutate the document head from React. A few
 * crawlers (Googlebot, GeminiBot) execute JS and will see this; HTML-only crawlers
 * (GPTBot, ClaudeBot) only see what the static prerendered HTML for the route ships.
 * The prerender step in scripts/prerender.mjs generates per-route HTML so both kinds
 * of crawlers get good metadata — this hook keeps the in-app navigation experience
 * consistent and updates the head for shared/copied links once JS has loaded.
 */

const SITE_URL = 'https://parkproof-app.web.app';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const JSON_LD_ID = 'route-jsonld';

export interface DocumentHeadInput {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** If true, robots won't index this page (use for /signin, /profile, etc.). */
  noindex?: boolean;
}

function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applyDocumentHead(input: DocumentHeadInput) {
  const canonical = input.canonicalPath
    ? `${SITE_URL}${input.canonicalPath.startsWith('/') ? '' : '/'}${input.canonicalPath}`
    : `${SITE_URL}/`;
  const ogImage = input.ogImage ?? DEFAULT_OG_IMAGE;

  document.title = input.title;
  setMeta('name', 'description', input.description);
  setMeta(
    'name',
    'robots',
    input.noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
  );
  setLink('canonical', canonical);

  setMeta('property', 'og:title', input.title);
  setMeta('property', 'og:description', input.description);
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', ogImage);

  setMeta('name', 'twitter:title', input.title);
  setMeta('name', 'twitter:description', input.description);
  setMeta('name', 'twitter:image', ogImage);

  // Replace the route-scoped JSON-LD block. The site-wide blocks in index.html
  // (Organization, WebSite, MobileApplication, FAQPage) stay untouched.
  let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (input.jsonLd) {
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_ID;
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(input.jsonLd);
  } else if (script) {
    script.remove();
  }
}

import { useEffect } from 'react';

export function useDocumentHead(input: DocumentHeadInput) {
  const key = JSON.stringify(input);
  useEffect(() => {
    applyDocumentHead(input);
    // We intentionally re-run when any field changes; serialize for a stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export { SITE_URL };
